import { Injectable } from '@angular/core';
import { CartItemResponse } from '@features/cart-item/types/cart-item.response';
import { NydusNetworkApiDataService } from '@rd/core/services/nydus-network-api-data/nydus-network-api-data.service';
import { ProductCategory } from '@rd/types';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CartPromocodeIndexResponse } from '../../models/cart-promo-code.response';
import { CartIndexResponse } from '../../models/cart.response';
import { CartItemTransformService } from '../../services/cart-item-api/cart-item-transform.service';
import { CartItem } from '../../types/cart-item.type';
import { Cart, CartPromocodePostParams } from '../../types/cart.type';

interface GiftCardCartItemParams {
  productType: ProductCategory.GiftCard | ProductCategory.PhysicalGiftCard;
  cashAmount: number;
  pointsAmount: number;
  supplierId?: string; // Will alias id from `denominationId` for Alfred ID/GiftCard.id,
  denominationId: string; // GiftCards option id
  quantity: number;
  orderDetails: {
    [key: string]: any;
  };
  disableShoppingCart: boolean;
}

interface MerchandiseCartItemParams {
  productType: ProductCategory.Merchandise;
  cashAmount: number;
  pointsAmount: number;
  supplierId: string; // merchandiseId
  quantity: number;
  orderDetails: {
    product_name: string;
    brand: string;
    points_percentage: number;
    recipient: {};
  };
}

interface CartItemPostParams {
  cartItem: GiftCardCartItemParams | MerchandiseCartItemParams;
}

export interface CartItemUpdateQuantityParams {
  id: CartItem['id'];
  quantity: number;
}

@Injectable({
  providedIn: 'root',
})
export class CartItemApiService {
  constructor(
    private nydusNetworkApiDataService: NydusNetworkApiDataService,
    private cartItemTransformService: CartItemTransformService,
  ) {}

  postCartItem(cartItemParams: CartItemPostParams): Observable<CartItem> {
    return this.nydusNetworkApiDataService
      .post<CartItemResponse>('cart/cart_items', { ...cartItemParams })
      .pipe(map((cartItemResponse) => cartItemResponse as CartItem));
  }

  getCart(): Observable<Cart> {
    return this.nydusNetworkApiDataService.get<CartIndexResponse>('cart').pipe(
      map((cartResponse: CartIndexResponse) => {
        const { included, data } = cartResponse;
        return this.cartItemTransformService.transformCartModel(included, data);
      }),
    );
  }

  applyPromocode(
    cartPromocodePostParams: CartPromocodePostParams,
  ): Observable<Cart> {
    return this.nydusNetworkApiDataService
      .post<CartPromocodeIndexResponse>('cart/promo_code_usage', {
        ...cartPromocodePostParams,
      })
      .pipe(
        map((cartResponse) => {
          const { included, data } = cartResponse;
          return this.cartItemTransformService.transformCartModel(
            included,
            data,
          );
        }),
      );
  }

  removePromocode(): Observable<void> {
    return this.nydusNetworkApiDataService.delete<void>(
      `cart/promo_code_usage`,
    );
  }

  updateCartItemQuantity(
    params: CartItemUpdateQuantityParams,
  ): Observable<void> {
    const { id, quantity } = params;

    return this.nydusNetworkApiDataService.put<void>(`cart/cart_items/${id}`, {
      quantity,
    });
  }

  removeCartItem(id: CartItem['id']): Observable<void> {
    return this.nydusNetworkApiDataService.delete<void>(
      `cart/cart_items/${id}`,
    );
  }
}
